import { Calendar } from "primereact/calendar";
import { useEffect, useState } from "react";
import { useClientTranslations } from "../../lib/i18n/client";
import styles from "./DatePickerInput.module.css";

// Custom navigation icons
const PrevIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		role="img"
		aria-label="Previous month"
	>
		<path
			d="M15 18L9 12L15 6"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const NextIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		role="img"
		aria-label="Next month"
	>
		<path
			d="M9 18L15 12L9 6"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

interface DatePickerInputProps {
	value?: Date | null;
	onChange: (date: Date | null) => void;
	placeholder?: string;
	className?: string;
}

export function DatePickerInput({
	value,
	onChange,
	placeholder = "DD/MM/YYYY",
	className = "",
}: DatePickerInputProps) {
	const [selectedDate, setSelectedDate] = useState<Date | null>(value || null);
	const { locale } = useClientTranslations();

	// Calculate date restrictions
	const today = new Date();
	const minDate = new Date(
		today.getFullYear() - 15,
		today.getMonth(),
		today.getDate(),
	); // 15 years ago
	const defaultViewDate = new Date(
		today.getFullYear() - 20,
		today.getMonth(),
		today.getDate(),
	); // 20 years ago

	useEffect(() => {
		setSelectedDate(value || null);
	}, [value]);

	const handleDateChange = (e: { value: Date | null | undefined }) => {
		const date = e.value || null;
		setSelectedDate(date);
		onChange(date);
	};

	return (
		<div className={`${styles.container} ${className}`} dir="ltr">
			<Calendar
				value={selectedDate}
				onChange={handleDateChange}
				dateFormat="dd/mm/yy"
				placeholder={placeholder}
				showIcon
				maxDate={minDate}
				viewDate={selectedDate || defaultViewDate}
				locale={locale}
				className={styles.calendar}
				inputClassName={styles.input}
				panelClassName={styles.panel}
				prevIcon={<PrevIcon />}
				nextIcon={<NextIcon />}
			/>
		</div>
	);
}
